import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { livyFetch } from 'src/lib/livyTransport';

/**
 * A Livy voice call. The browser talks to OpenAI GPT-Live over WebRTC; the proxy creates the session so the OpenAI
 * key never reaches the page. GPT-Live only listens and speaks: every real question comes back as a delegation, which
 * the caller answers through the ordinary chat route. Hanging up is the proxy's job, so the seconds billed are
 * OpenAI's own and a page that never hangs up is still cut off at the limit.
 */

export class VoiceError extends Error {
  constructor(code) {
    super(code);
    this.code = code;
  }
}

/** Under the 500-token append limit, cut at a sentence end where there is one. */
export const SAY_MAX = 1800;
export function forSpeech(text) {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= SAY_MAX) return flat;
  const cut = flat.slice(0, SAY_MAX);
  const end = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('? '), cut.lastIndexOf('! '));
  return end > 0 ? cut.slice(0, end + 1) : cut;
}
export function voiceErrorCode(status, code) {
  if (status === 403 && code === 'voice_off') return 'off';
  if (status === 429) return 'quota';
  if (status === 409 && code === 'replay_read_only') return 'readonly';
  return 'connect';
}

/** One oai-events message to the event the call cares about; null for everything else. */
export function toVoiceEvent(raw) {
  var _m$delegation, _m$delta, _m$delta2, _m$reason;
  let m;
  try {
    m = JSON.parse(raw);
  } catch (_unused) {
    return null;
  }
  switch (m.type) {
    case 'session.started':
      return {
        type: 'started'
      };
    case 'session.delegation.created':
      return (_m$delegation = m.delegation) !== null && _m$delegation !== void 0 && _m$delegation.id ? {
        type: 'delegation',
        id: m.delegation.id
      } : null;
    case 'session.input_transcript.delta':
      return {
        type: 'heard',
        delta: (_m$delta = m.delta) !== null && _m$delta !== void 0 ? _m$delta : ''
      };
    case 'session.output_transcript.delta':
      return {
        type: 'said',
        delta: (_m$delta2 = m.delta) !== null && _m$delta2 !== void 0 ? _m$delta2 : ''
      };
    case 'session.closed':
      return {
        type: 'closed',
        reason: (_m$reason = m.reason) !== null && _m$reason !== void 0 ? _m$reason : 'closed'
      };
    case 'error':
      return {
        type: 'error',
        code: 'unknown'
      };
    default:
      return null;
  }
}
const ICE_WAIT_MS = 10000;
function iceGathered(pc) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise(resolve => {
    const listening = new AbortController();
    const timer = setTimeout(() => {
      listening.abort();
      resolve();
    }, ICE_WAIT_MS);
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState !== 'complete') return;
      clearTimeout(timer);
      listening.abort();
      resolve();
    }, {
      signal: listening.signal
    });
  });
}

/**
 * Starts a call. `audio` must be created in the click handler that started the call, or the browser's autoplay rule
 * keeps Livy silent.
 */
export async function startVoiceCall(args) {
  const config = args.config,
    token = args.token,
    surface = args.surface,
    conversationId = args.conversationId,
    resident = args.resident,
    audio = args.audio,
    onEvent = args.onEvent;
  let mic;
  try {
    mic = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true
      }
    });
  } catch (_unused2) {
    throw new VoiceError('mic');
  }
  const pc = new RTCPeerConnection();
  const hangUpLocally = () => {
    mic.getTracks().forEach(t => t.stop());
    pc.close();
    audio.srcObject = null;
  };
  try {
    var _pc$localDescription;
    mic.getTracks().forEach(t => pc.addTrack(t, mic));
    pc.ontrack = e => {
      audio.srcObject = e.streams[0];
      void audio.play().catch(() => undefined);
    };
    // Opened before the offer, so the offer carries it.
    const channel = pc.createDataChannel('oai-events');
    channel.onmessage = e => {
      const event = toVoiceEvent(String(e.data));
      if (event) onEvent(event);
    };
    await pc.setLocalDescription(await pc.createOffer());
    await iceGathered(pc);
    const res = await livyFetch(config, token, {
      method: 'POST',
      path: '/voice/session',
      body: JSON.stringify(_objectSpread({
        sdp: (_pc$localDescription = pc.localDescription) === null || _pc$localDescription === void 0 ? void 0 : _pc$localDescription.sdp,
        surface,
        conversationId
      }, resident ? {
        resident
      } : {}))
    });
    if (!res.ok) {
      const code = (await res.json().catch(() => ({}))).error;
      throw new VoiceError(voiceErrorCode(res.status, code));
    }
    const _ref = await res.json(),
      sdp = _ref.sdp,
      sessionId = _ref.sessionId,
      maxSeconds = _ref.maxSeconds;
    await pc.setRemoteDescription({
      type: 'answer',
      sdp
    });
    const append = (type, delegationId, content) => {
      if (channel.readyState === 'open') channel.send(JSON.stringify({
        type,
        delegation_id: delegationId,
        content
      }));
    };
    let ended = null;
    return {
      sessionId,
      maxSeconds,
      mute: on => mic.getAudioTracks().forEach(t => t.enabled = !on),
      say: (id, text) => append('session.commentary.append', id, forSpeech(text)),
      note: (id, text) => append('session.thinking.append', id, text),
      // The proxy closes the session first and only then does the page drop its leg, so the close is confirmed by
      // OpenAI rather than inferred from a dropped connection.
      end: () => ended !== null && ended !== void 0 ? ended : ended = livyFetch(config, token, {
        method: 'POST',
        path: '/voice/end',
        body: JSON.stringify({
          sessionId
        })
      }).then(r => r.ok ? r.json() : null).then(out => {
        var _out$seconds;
        return (_out$seconds = out === null || out === void 0 ? void 0 : out.seconds) !== null && _out$seconds !== void 0 ? _out$seconds : null;
      }).catch(() => null).finally(hangUpLocally)
    };
  } catch (e) {
    hangUpLocally();
    throw e instanceof VoiceError ? e : new VoiceError('connect');
  }
}